/**
 * Base API Service
 * Handles low-level HTTP requests with authentication
 */

import { fetchWithRetry, NetworkRequestError, NETWORK_ERROR_CODE, OFFLINE_MESSAGE, setNetworkErrorHandler } from '../utils/networkRetry';
import { getDeviceTimezone } from '../utils/deviceTimezone';
import { newRefreshAttemptId } from '../utils/refreshAttempt';

export { setNetworkErrorHandler, NETWORK_ERROR_CODE, OFFLINE_MESSAGE };

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

export class ApiError extends Error {
    constructor(statusCode, message, payload = null) {
        super(message);
        this.name = 'ApiError';
        this.statusCode = statusCode;
        // Machine-readable reason from the backend (e.g. 'email_verification_required',
        // 'rate_limit_exceeded') so callers can branch on the cause rather than
        // pattern-matching the human-facing message.
        this.code = payload?.error ?? payload?.code ?? null;
        this.payload = payload;
    }

    /** True when the request never got a response (offline, timeout, DNS). */
    get isNetworkError() {
        return this.statusCode === 0 && this.code === NETWORK_ERROR_CODE;
    }
}

/** Turn a failed-connection error into an ApiError callers can show directly. */
function toNetworkApiError(error) {
    // timedOut: the last attempt hit our client timeout rather than failing to connect.
    const timedOut = error?.cause?.name === 'AbortError' || error?.cause?.name === 'TimeoutError';
    return new ApiError(0, error?.message || OFFLINE_MESSAGE, { code: NETWORK_ERROR_CODE, attempts: error?.attempts ?? 1, timedOut });
}

// Token management (you might want to use localStorage or a state management solution)
const TOKEN_KEY = 'aluvo_access_token';
const REFRESH_TOKEN_KEY = 'aluvo_refresh_token';

export const TokenStorage = {
    getAccessToken: () => localStorage.getItem(TOKEN_KEY),
    getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
    saveTokens: (accessToken, refreshToken) => {
        localStorage.setItem(TOKEN_KEY, accessToken);
        if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    },
    clearTokens: () => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
    },
};

let logoutHandler = null;
let dobRequiredHandler = null;

export function setLogoutHandler(fn) {
    logoutHandler = fn;
}

export function setDobRequiredHandler(fn) {
    dobRequiredHandler = fn;
}

// ── Token refresh ─────────────────────────────────────────────────────────────
//
// Refresh tokens are single use. Several tabs (or several requests in one tab)
// can hit an expired access token at the same moment; only one of them may
// exchange the refresh token. Coordination, strongest first:
//   1. one in-flight refresh per tab;
//   2. a cross-tab lock: Web Locks where available, else a short localStorage
//      lease. Whoever gets the lock second sees that the stored access token
//      already changed and simply uses it;
//   3. if a refresh is still rejected as reuse, but another tab has meanwhile
//      stored a new session, adopt that session instead of signing out.
// Each refresh attempt carries one random id, repeated on its network retries,
// so a retry after a lost response gets the replacement the server issued.

const REFRESH_LOCK_NAME = 'aluvo-token-refresh';
const REFRESH_LEASE_KEY = 'aluvo_refresh_lease';
const LEASE_MS = 10000;
const LEASE_POLL_MS = 50;
const LEASE_MAX_WAIT_MS = 15000;

let refreshInFlight = null;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function readLease() {
    try {
        return JSON.parse(localStorage.getItem(REFRESH_LEASE_KEY) || 'null');
    } catch {
        return null;
    }
}

/** Cross-tab mutual exclusion without Web Locks (best effort; step 3 backs it up). */
async function withStorageLease(fn) {
    const owner = newRefreshAttemptId();
    const deadline = Date.now() + LEASE_MAX_WAIT_MS;
    try {
        while (Date.now() < deadline) {
            const held = readLease();
            if (!held || held.until < Date.now()) {
                localStorage.setItem(REFRESH_LEASE_KEY, JSON.stringify({ owner, until: Date.now() + LEASE_MS }));
                await sleep(LEASE_POLL_MS); // racing tabs: the last writer wins
                if (readLease()?.owner === owner) {
                    try {
                        return await fn();
                    } finally {
                        if (readLease()?.owner === owner) localStorage.removeItem(REFRESH_LEASE_KEY);
                    }
                }
            }
            await sleep(LEASE_POLL_MS);
        }
    } catch {
        // localStorage unavailable: fall through without the lease.
    }
    return fn();
}

function withRefreshLock(fn) {
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        return navigator.locks.request(REFRESH_LOCK_NAME, fn);
    }
    return withStorageLease(fn);
}

async function exchangeRefreshToken(refreshToken) {
    const refreshAttemptId = newRefreshAttemptId();
    const response = await fetchWithRetry(
        (signal) => fetch(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // Keeps users.timezone current for long-lived sessions and travellers.
            body: JSON.stringify({ refreshToken, refreshAttemptId, timezone: getDeviceTimezone() }),
            signal,
        }),
        { method: 'POST' },
    );

    if (!response.ok) {
        // Another tab won the exchange and stored its session: use that one.
        const stored = TokenStorage.getRefreshToken();
        if (response.status === 401 && stored && stored !== refreshToken) {
            return TokenStorage.getAccessToken();
        }
        throw new Error('Refresh failed');
    }

    const data = await response.json();
    const tokens = data.data?.tokens ?? data.tokens;
    TokenStorage.saveTokens(tokens.accessToken, tokens.refreshToken);
    return tokens.accessToken;
}

/**
 * Get a usable access token after `staleAccessToken` was rejected. Returns the
 * new access token; throws when the session cannot be renewed.
 */
function refreshAccessToken(staleAccessToken) {
    if (!refreshInFlight) {
        refreshInFlight = withRefreshLock(async () => {
            const current = TokenStorage.getAccessToken();
            // Another tab refreshed while we waited for the lock.
            if (current && current !== staleAccessToken) return current;

            const refreshToken = TokenStorage.getRefreshToken();
            if (!refreshToken) throw new Error('No refresh token');
            return exchangeRefreshToken(refreshToken);
        }).finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
}

/**
 * Make an authenticated API request
 * @param {string} path - API endpoint path
 * @param {Object} options - Request options
 * @param {string} options.method - HTTP method (default: 'GET')
 * @param {Object} options.body - Request body (will be JSON stringified)
 * @param {Object} options.params - URL query parameters
 * @param {boolean} options.authenticated - Whether to include auth token (default: true)
 * @param {boolean} isRetry - Internal flag for retry logic
 * @returns {Promise<any>}
 */
export async function apiRequest(path, options = {}, isRetry = false) {
    // quiet: background call (diagnostics) — never raises the offline banner.
    const { method = 'GET', body, params, authenticated = true, headers: extraHeaders = {}, retry, quiet = false } = options;

    // Build URL with query parameters
    let url = `${API_BASE_URL}${path}`;
    if (params) {
        const queryString = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');
        if (queryString) url += `?${queryString}`;
    }

    // Identifies the client platform for server-side activation analytics.
    // X-Aluvo-Features: this client can show a coin held until the email is
    // verified (first quiz before verification).
    const headers = { 'Content-Type': 'application/json', 'X-Aluvo-Platform': 'web', 'X-Aluvo-Features': 'pending-coin', ...extraHeaders };

    const sentAccessToken = authenticated ? TokenStorage.getAccessToken() : null;
    if (sentAccessToken) headers['Authorization'] = `Bearer ${sentAccessToken}`;

    let response;
    try {
        response = await fetchWithRetry(
            (signal) => fetch(url, { method, headers, body: body ? JSON.stringify(body) : undefined, signal }),
            { method, retry, notify: !quiet },
        );
    } catch (error) {
        if (error instanceof NetworkRequestError) throw toNetworkApiError(error);
        throw error;
    }

    // Token expired — try to refresh once then retry
    if (response.status === 401 && authenticated && !isRetry) {
        try {
            await refreshAccessToken(sentAccessToken);
            return apiRequest(path, options, true); // retry with new token
        } catch (refreshError) {
            // Offline during refresh: keep the session, surface the network error.
            if (refreshError instanceof NetworkRequestError) throw toNetworkApiError(refreshError);
            // Refresh failed — force logout
            TokenStorage.clearTokens();
            if (logoutHandler) logoutHandler();
            throw new ApiError(401, 'Session expired. Please log in again.');
        }
    }

    // 204 No Content — backend returns empty body
    if (response.status === 204 || response.headers.get('content-length') === '0') {
        return null;
    }

    const data = await response.json();

    // DOB required check (403 with requiresDob flag)
    if (response.status === 403 && data?.requiresDob === true) {
        if (dobRequiredHandler) {
            dobRequiredHandler();
        }
        throw new ApiError(403, 'Date of birth required to continue', data);
    }

    if (!response.ok) {
        // Validation responses carry the useful message inside `errors[]`;
        // the top-level message is just a generic "Validation failed".
        const fieldError = Array.isArray(data?.errors)
            ? data.errors.find((e) => e?.message)?.message
            : null;
        throw new ApiError(response.status, fieldError || data?.message || 'Request failed', data);
    }

    // Support both { success: true, data: { ... } } and flat response shapes
    return data.data ?? data;
}

/**
 * An authenticated binary GET (e.g. a private image), refreshing the session
 * once on a 401 like apiRequest. Never cached by the browser.
 * @param {string} path - API endpoint path (with its query)
 * @returns {Promise<Blob>}
 */
export async function apiBlob(path, isRetry = false) {
    const token = TokenStorage.getAccessToken();
    const headers = { 'X-Aluvo-Platform': 'web' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    let response;
    try {
        response = await fetchWithRetry(
            (signal) => fetch(`${API_BASE_URL}${path}`, { headers, cache: 'no-store', signal }),
            { method: 'GET' },
        );
    } catch (error) {
        if (error instanceof NetworkRequestError) throw toNetworkApiError(error);
        throw error;
    }

    if (response.status === 401 && !isRetry) {
        try {
            await refreshAccessToken(token);
            return apiBlob(path, true);
        } catch {
            TokenStorage.clearTokens();
            if (logoutHandler) logoutHandler();
            throw new ApiError(401, 'Session expired. Please log in again.');
        }
    }
    if (!response.ok) {
        let data = null;
        try { data = await response.json(); } catch { /* not JSON */ }
        throw new ApiError(response.status, data?.message || 'Request failed', data);
    }
    return response.blob();
}

/**
 * Make an authenticated multipart/form-data upload request
 * @param {string} path - API endpoint path
 * @param {FormData} formData - FormData object to send
 * @param {boolean} isRetry - Internal flag for retry logic
 * @returns {Promise<any>}
 */
export async function apiUpload(path, formData, isRetry = false, { method = 'POST' } = {}) {
    const token = TokenStorage.getAccessToken();

    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    let response;
    try {
        response = await fetchWithRetry(
            (signal) => fetch(`${API_BASE_URL}${path}`, { method, headers, body: formData, signal }),
            { method, retry: { retries: 0, timeoutMs: 60000 } },
        );
    } catch (error) {
        if (error instanceof NetworkRequestError) throw toNetworkApiError(error);
        throw error;
    }

    if (response.status === 401 && !isRetry) {
        try {
            await refreshAccessToken(token);
            return apiUpload(path, formData, true, { method });
        } catch {
            TokenStorage.clearTokens();
            if (logoutHandler) logoutHandler();
            throw new ApiError(401, 'Session expired. Please log in again.');
        }
    }

    const data = await response.json();

    if (!response.ok) {
        throw new ApiError(response.status, data?.message || `Upload failed (${response.status})`);
    }

    return data.data ?? data;
}
